import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import { useBranding } from "@/lib/branding";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Bookmark, ArrowRight, Trash2, Highlighter, Download } from "lucide-react";
import { HIGHLIGHT_COLORS } from "@/components/HighlightContainer";
import { toast } from "sonner";

export default function Bookmarks() {
  const { branding } = useBranding();
  const [bookmarks, setBookmarks] = useState([]);
  const [highlights, setHighlights] = useState([]);
  const [bundling, setBundling] = useState(false);

  const load = () => {
    api.get("/bookmarks").then(r => setBookmarks(r.data));
    api.get("/highlights").then(r => setHighlights(r.data));
  };
  useEffect(() => { load(); }, []);

  const removeBookmark = async (chapter_id, section_idx) => {
    await api.post("/bookmarks", { chapter_id, section_idx });
    toast.success("Bookmark removed");
    load();
  };

  const removeHighlight = async (id) => {
    await api.delete(`/highlights/${id}`);
    toast.success("Highlight removed");
    load();
  };

  const downloadAllGuides = async () => {
    setBundling(true);
    try {
      const r = await api.get("/study-guide");
      const primary = branding.primary_color || "265 55% 28%";
      const brand = branding.brand_name || "DyNamic Tax School";
      const win = window.open("", "_blank");
      if (!win) { toast.error("Popup blocked"); setBundling(false); return; }
      const chapterHtml = r.data.map(d => {
        const groups = {};
        d.facts.forEach(f => { (groups[f.heading] = groups[f.heading] || []).push(f.fact); });
        return `<section class="chapter">
          <div class="brand">${brand}</div>
          <h1>Ch. ${d.chapter_id} — ${d.title}</h1>
          <div class="cat">${d.category} · Cheat Sheet</div>
          <p class="summary">${d.summary}</p>
          ${d.facts.length === 0 ? '<p class="empty">No memorization facts in this chapter.</p>' :
            Object.entries(groups).map(([h, arr]) => `<h2>${h}</h2><ul>${arr.map(a => `<li>${a.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")}</li>`).join("")}</ul>`).join("")}
        </section>`;
      }).join("");
      win.document.write(`<!doctype html><html><head><title>${brand} - Complete Cheat Sheet Bundle</title>
        <style>
          @page { margin: 0.75in; }
          body { font-family: Georgia, serif; margin: 0; padding: 0; color: #111; background: #f6f5ee; }
          .chapter { max-width: 780px; margin: 0 auto 40px; background: #fffdf4; border: 1px solid #ccc; padding: 40px; page-break-after: always; }
          .chapter:last-child { page-break-after: auto; }
          .brand { letter-spacing: 8px; text-transform: uppercase; font-size: 10px; color: hsl(${primary}); }
          h1 { font-size: 34px; margin: 6px 0 0; font-weight: 500; }
          .cat { font-size: 11px; text-transform: uppercase; letter-spacing: 3px; color: #666; margin-top: 6px; }
          .summary { color: #444; margin: 12px 0 20px; font-size: 14px; }
          h2 { font-size: 16px; margin: 18px 0 4px; color: #222; border-bottom: 1px solid #ddd; padding-bottom: 4px; }
          ul { margin: 4px 0 10px 20px; padding: 0; }
          li { margin-bottom: 3px; font-family: 'Helvetica Neue', Arial, sans-serif; font-size: 12px; line-height: 1.4; }
          .empty { font-family: sans-serif; color: #999; font-style: italic; font-size: 12px; }
          strong { font-weight: 700; }
        </style></head><body>${chapterHtml}
        <script>window.onload = () => setTimeout(() => window.print(), 500);</script>
      </body></html>`);
      win.document.close();
    } catch (e) { toast.error("Bundle failed"); }
    finally { setBundling(false); }
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto p-6 lg:p-12">
        <div className="flex items-baseline justify-between gap-4 flex-wrap mb-8">
          <div>
            <div className="label-caps mb-3">Your saved sections & highlights</div>
            <h1 className="font-serif text-4xl lg:text-6xl leading-none">Study bench.</h1>
          </div>
          <Button onClick={downloadAllGuides} disabled={bundling} data-testid="download-all-guides-btn" size="lg">
            <Download size={16} className="mr-2" /> {bundling ? "Building…" : "Download all 13 cheat sheets"}
          </Button>
        </div>

        <section className="mb-12">
          <div className="flex items-center gap-2 mb-4">
            <Bookmark size={18} className="text-primary" />
            <h2 className="font-serif text-2xl">Bookmarks</h2>
            <span className="label-caps text-[10px]">({bookmarks.length})</span>
          </div>
          {bookmarks.length === 0 ? (
            <Card className="p-6 text-sm text-muted-foreground">Tap the bookmark icon on any section to save it here.</Card>
          ) : (
            <div className="space-y-3" data-testid="bookmarks-list">
              {bookmarks.map(b => (
                <Card key={b.id} className="p-5 flex items-center justify-between gap-4 hover:border-primary transition-colors" data-testid={`bookmark-${b.chapter_id}-${b.section_idx}`}>
                  <div className="min-w-0">
                    <div className="label-caps mb-1">Ch. {b.chapter_id} · {b.chapter_title}</div>
                    <div className="font-serif text-xl leading-tight truncate">{b.section_heading}</div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Link to={`/course/${b.chapter_id}?section=${b.section_idx}`}>
                      <Button size="sm" variant="outline">Open <ArrowRight size={12} className="ml-1" /></Button>
                    </Link>
                    <Button size="icon" variant="ghost" onClick={() => removeBookmark(b.chapter_id, b.section_idx)}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </section>

        <section>
          <div className="flex items-center gap-2 mb-4">
            <Highlighter size={18} className="text-accent" />
            <h2 className="font-serif text-2xl">Highlights</h2>
            <span className="label-caps text-[10px]">({highlights.length})</span>
          </div>
          {highlights.length === 0 ? (
            <Card className="p-6 text-sm text-muted-foreground">Select any sentence inside a chapter and tap "Highlight" — it shows up here.</Card>
          ) : (
            <div className="space-y-3" data-testid="highlights-list">
              {highlights.map(h => (
                <Card key={h.id} className="p-5" data-testid={`highlight-${h.id}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="label-caps mb-1">Ch. {h.chapter_id} · {h.chapter_title} → {h.section_heading}</div>
                      <div className="mt-2 text-base leading-relaxed">
                        <mark style={{background: HIGHLIGHT_COLORS[h.color] || HIGHLIGHT_COLORS.yellow, padding: "0 4px", borderRadius: "2px"}}>{h.text}</mark>
                      </div>
                    </div>
                    <div className="flex flex-col gap-2 shrink-0">
                      <Link to={`/course/${h.chapter_id}?section=${h.section_idx}`}>
                        <Button size="sm" variant="outline">Open</Button>
                      </Link>
                      <Button size="icon" variant="ghost" onClick={() => removeHighlight(h.id)}>
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </section>
      </div>
    </Layout>
  );
}
